import React, { useContext } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import Button from "components/buttons/Button"
import cc from "classcat"
import Highlighter from "react-highlight-words"
import Slider from "react-slick"
import RequestButton from "components/molecules/RequestButton"
import { toDateTimeFormat, toTimeFormat } from "utils/date"
import { LocaleContext } from "contexts"
import Divided from "components/organisms/common/Divided"
import useTranslations from "hooks/useTranslations"
import H2 from "components/atoms/H2"

const settings = {
  infinite: true,
  slidesToScroll: 1,
  speed: 1000,
  autoplaySpeed: 2500,
  autoplay: true,
  dots: false,
  arrows: false,
  fade: true,
  pauseOnHover: false,
}

type HeroProps = {
  Image: (props?: any) => JSX.Element
  bgColor: string
  slides: string[]
  description: string | string[]
  searchWords: string[]
  HeroButton: (props?: any) => JSX.Element
  buttonProps?: any
  descriptionBelow: boolean
  hideMobileImage: boolean
  showButton: boolean
  video?: string
  poster?: string
}

const Hero = ({
  Image,
  bgColor,
  slides,
  description,
  searchWords = [],
  HeroButton,
  buttonProps,
  descriptionBelow = false,
  hideMobileImage = false,
  showButton = true,
  video,
  poster,
}: HeroProps) => {
  const slidesData = slides.map((slide) => ({ title: slide }))

  const ButtonCTA = HeroButton || RequestButton

  return (
    <Divided
      Image={Image}
      video={video}
      theme={bgColor}
      noVerticalPadding
      contentClassName="pb-12 sm:pb-16 md:pb-20 lg:pt-14"
      className="mt-16 sm:mt-16 lg:mt-20"
      hideMobileImage={hideMobileImage}
    >
      <div className="space-y-8">
        <div
          className={cc([
            "flex flex-col space-y-8 sm:text-center lg:text-left",
            descriptionBelow ? "space-y-reverse" : "",
          ])}
        >
          {description && (
            <p className="m-0 animate-appear font-display text-xl text-white sm:text-center lg:text-left lg:text-2xl lg:leading-tight">
              {Array.isArray(description) ? (
                description.map((desc, index) => (
                  <span key={index} className="">
                    {desc}
                    {index < description.length - 1 && <br />}{" "}
                  </span>
                ))
              ) : (
                <span className="">{description}</span>
              )}
            </p>
          )}
          <Slider
            slidesToShow={1}
            {...settings}
            className={descriptionBelow ? "order-first" : ""}
          >
            {slidesData.map((item) => (
              <H2
                size="lg"
                className={cc([
                  "animate-appear space-y-8 tracking-wide [text-wrap:balance]",
                  {
                    "text-white": !bgColor || bgColor === "darkgreen",
                    "text-zinc-800": bgColor === "yellow" || "green",
                  },
                ])}
              >
                <Highlighter
                  highlightClassName={cc([
                    "underline bg-transparent underline-offset-8",
                    {
                      "text-zinc-800": !bgColor || bgColor === "darkgreen",
                      "text-white": bgColor === "yellow" || "green",
                    },
                  ])}
                  searchWords={searchWords || ["prevent", "protect", "impact"]}
                  autoEscape
                  textToHighlight={item.title}
                />
              </H2>
            ))}
          </Slider>
        </div>
        {showButton ? (
          <div className="mx-auto flex max-w-lg items-start sm:justify-center lg:mx-0 lg:justify-start">
            <ButtonCTA bgStyle="black" left {...buttonProps} />
          </div>
        ) : null}
      </div>
    </Divided>
  )
}

const HeroImage = ({ className }) => {
  const data = useStaticQuery(graphql`
    {
      hero: file(relativePath: { eq: "pro/impact.jpg" }) {
        childImageSharp {
          gatsbyImageData(quality: 90)
        }
      }
    }
  `)

  return (
    <GatsbyImage
      loading="eager"
      image={getImage(data.hero)}
      className={className}
      imgStyle={{
        objectFit: "cover",
        objectPosition: "top",
      }}
      alt="Webinar hero image"
    />
  )
}

const FormButton = ({ bgStyle = "primary", left, time, end }) => {
  const { webinarHeroButton } = useTranslations()
  const { locale } = useContext(LocaleContext)
  return (
    <div
      className={cc([
        "flex flex-col items-start justify-start space-y-6",
        left ? "" : "mx-auto",
      ])}
    >
      <Button
        bgStyle={bgStyle}
        label={webinarHeroButton}
        link="#webinar-list"
      />
      {time && (
        <div className="font-display text-xl text-white lg:text-2xl">
          <time dateTime={`${time}`}>
            {toDateTimeFormat(time, locale)}
            {end ? ` – ${toTimeFormat(end)}` : null}
          </time>
        </div>
      )}
    </div>
  )
}

type HeroWebinarProps = {
  title: string
  time?: Date
  end?: Date
  highlights?: string[]
  bgColor?: string
  Image?: (props?: any) => JSX.Element
  description?: string
  descriptionBelow?: boolean
  showButton?: boolean
  hideMobileImage?: boolean
  video?: string
  HeroButton?: (theme?) => JSX.Element
  poster?: string
}

export default function HeroWebinar({
  title,
  time,
  end,
  highlights,
  bgColor = "yellow",
  Image = HeroImage,
  description,
  descriptionBelow,
  showButton,
  hideMobileImage,
  video,
  HeroButton,
  poster,
}: HeroWebinarProps) {
  return (
    <Hero
      Image={Image}
      slides={[title]}
      searchWords={highlights || [""]}
      HeroButton={HeroButton || FormButton}
      buttonProps={{ time, end }}
      description={description}
      descriptionBelow={descriptionBelow}
      bgColor={bgColor}
      showButton={showButton}
      hideMobileImage={hideMobileImage}
      video={video}
      poster={poster}
    />
  )
}
